
import { supabase } from "@/integrations/supabase/client";
import { deleteZoomMeeting, updateZoomMeeting } from './zoom-meeting-actions';

interface CancelBookingParams {
  bookingId: string;
  reason?: string;
  meetingLinkId?: string; // Added this parameter
}

interface RescheduleBookingParams {
  bookingId: string;
  newDate: Date;
  reason?: string;
  meetingLinkId?: string; // Added this parameter
}

interface DeclineBookingParams {
  bookingId: string;
  reason?: string;
  meetingLinkId?: string; // Added this parameter
}

/**
 * Cancel a booking
 */
export const cancelBooking = async ({ bookingId, reason, meetingLinkId }: CancelBookingParams) => {
  try {
    // First, get the booking details including calendar_event_id and host info
    const { data: bookingData, error: fetchError } = await supabase
      .from('bookings')
      .select(`
        calendar_event_id, 
        meeting_link_id, 
        status,
        meeting_links!bookings_meeting_link_id_fkey(user_id)
      `)
      .eq('id', bookingId)
      .single();
      
    if (fetchError) {
      console.error('Error fetching booking details:', fetchError);
      throw fetchError;
    }

    // Check if booking is already cancelled
    if (bookingData.status === 'canceled') {
      console.log('ℹ️ Booking is already cancelled');
      return {
        success: true,
        data: bookingData,
        alreadyCancelled: true
      };
    }

    // Step 1: Cancel the calendar event FIRST (if it exists)
    const hostUserId = bookingData.meeting_links?.user_id;
    if (bookingData.calendar_event_id && hostUserId) {
      try {
        console.log(`🗑️ Cancelling calendar event: ${bookingData.calendar_event_id} for host: ${hostUserId}`);
        
        const { data: calendarResponse, error: calendarError } = await supabase.functions.invoke('google-calendar', {
          body: {
            action: 'cancel_event',
            eventId: bookingData.calendar_event_id,
            userId: hostUserId,
            sendNotifications: true,
            reason: reason
          }
        });

        if (calendarError) {
          console.error('❌ Calendar deletion failed:', calendarError);
          throw new Error(`Calendar deletion failed: ${calendarError.message || calendarError}`);
        }
        
      } catch (calendarException) {
        console.error('❌ Exception during calendar deletion:', calendarException);
        throw new Error(`Calendar deletion failed: ${calendarException.message || calendarException}`);
      }
    } else {
      console.log('ℹ️ No calendar event to cancel (calendar_event_id or hostUserId missing)', {
        calendar_event_id: bookingData.calendar_event_id,
        hostUserId: hostUserId
      });
    }

    // Step 1.5: Cancel Zoom meeting if it exists
    if (bookingData.zoom_meeting_id && hostUserId) {
      try {
        
        const zoomResult = await deleteZoomMeeting({
          userId: hostUserId,
          meetingId: bookingData.zoom_meeting_id
        });

        if (zoomResult.success) {
        } else {
          console.error('❌ Zoom meeting cancellation failed:', zoomResult.error);
          // Don't throw here - we can still cancel the booking even if Zoom fails
        }
      } catch (zoomException) {
        console.error('❌ Exception during Zoom meeting cancellation:', zoomException);
        // Don't throw here - we can still cancel the booking even if Zoom fails
      }
    } else {
      console.log('ℹ️ No Zoom meeting to cancel (zoom_meeting_id or hostUserId missing)', {
        zoom_meeting_id: bookingData.zoom_meeting_id,
        hostUserId: hostUserId
      });
    }

    // Step 2: Update the database status ONLY after calendar cancellation succeeds
    console.log('📝 Updating booking status in database...');
    const { data, error } = await supabase
      .from('bookings')
      .update({
        status: 'canceled',
        message: reason || 'Canceled by host'
      })
      .eq('id', bookingId)
      .select()
      .single();
      
    if (error) {
      console.error('❌ Database update failed:', error);
      throw new Error(`Database update failed: ${error.message}`);
    }

    return {
      success: true,
      data
    };
  } catch (error: any) {
    console.error('❌ Error canceling booking:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete a booking - cancels calendar event and removes from database
 */
export const deleteBooking = async ({ bookingId }: { bookingId: string }) => {
  try {
    // First, get the booking details including calendar_event_id and host info
    const { data: bookingData, error: fetchError } = await supabase
      .from('bookings')
      .select(`
        calendar_event_id,
        meeting_link_id,
        status,
        booker_name,
        booker_email,
        booking_date,
        meeting_links!bookings_meeting_link_id_fkey(user_id)
      `)
      .eq('id', bookingId)
      .single();
      
    if (fetchError) {
      console.error('Error fetching booking details for deletion:', fetchError);
      throw fetchError;
    }

    // Step 1: Cancel the calendar event FIRST (if it exists)
    const hostUserId = bookingData.meeting_links?.user_id;
    if (bookingData.calendar_event_id && hostUserId) {
      try {
        console.log(`🗑️ Cancelling calendar event before deletion: ${bookingData.calendar_event_id} for host: ${hostUserId}`);
        
        const { data: calendarResponse, error: calendarError } = await supabase.functions.invoke('google-calendar', {
          body: {
            action: 'cancel_event',
            eventId: bookingData.calendar_event_id,
            userId: hostUserId,
            sendNotifications: true,
            reason: 'Booking deleted by host'
          }
        });

        if (calendarError) {
          console.error('❌ Calendar deletion failed:', calendarError);
          throw new Error(`Calendar deletion failed: ${calendarError.message || calendarError}`);
        }
        
      } catch (calendarException) {
        console.error('❌ Exception during calendar deletion:', calendarException);
        throw new Error(`Calendar deletion failed: ${calendarException.message || calendarException}`);
      }
    } else {
      console.log('ℹ️ No calendar event to cancel before deletion', {
        calendar_event_id: bookingData.calendar_event_id,
        hostUserId: hostUserId
      });
    }

    // Step 2: Delete from database ONLY after calendar cancellation succeeds
    console.log('🗑️ Deleting booking from database...');
    const { error: deleteError } = await supabase
      .from('bookings')
      .delete()
      .eq('id', bookingId);
      
    if (deleteError) {
      console.error('❌ Database deletion failed:', deleteError);
      throw new Error(`Database deletion failed: ${deleteError.message}`);
    }

    return {
      success: true,
      data: { id: bookingId }
    };
  } catch (error: any) {
    console.error('❌ Error deleting booking:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Reschedule a booking - updates calendar event and database with new date/time
 */
export const rescheduleBooking = async ({ bookingId, newDate, reason }: RescheduleBookingParams) => {
  try {
    // First, get the booking details including calendar_event_id, host info, duration, and Zoom meeting info
    const { data: bookingData, error: fetchError } = await supabase
      .from('bookings')
      .select(`
        calendar_event_id, 
        meeting_link_id, 
        status,
        booker_name,
        booker_email,
        booking_date,
        zoom_meeting_id,
        zoom_join_url,
        zoom_start_url,
        meeting_links!bookings_meeting_link_id_fkey(user_id, duration, location_type)
      `)
      .eq('id', bookingId)
      .single();
      
    if (fetchError) {
      console.error('Error fetching booking details for reschedule:', fetchError);
      throw fetchError;
    }

    // Check if booking can be rescheduled
    if (bookingData.status === 'canceled') {
      console.log('ℹ️ Cannot reschedule a cancelled booking');
      return {
        success: false,
        error: 'Cannot reschedule a cancelled booking'
      };
    }

    const hostUserId = bookingData.meeting_links?.user_id;
    const duration = bookingData.meeting_links?.duration || 30; // Default 30 minutes
    const locationType = bookingData.meeting_links?.location_type;

    // Calculate end time based on duration
    const startTime = new Date(newDate);
    const endTime = new Date(startTime.getTime() + (duration * 60 * 1000));

    // Step 1: Handle calendar event (reschedule existing OR create new)
    if (bookingData.calendar_event_id && hostUserId) {
      // Reschedule existing calendar event
      try {
        
        const { data: calendarResponse, error: calendarError } = await supabase.functions.invoke('google-calendar', {
          body: {
            action: 'reschedule_event',
            eventId: bookingData.calendar_event_id,
            userId: hostUserId,
            newStartTime: startTime.toISOString(),
            newEndTime: endTime.toISOString(),
            sendNotifications: true,
            reason: reason
          }
        });

        if (calendarError) {
          console.error('❌ Calendar reschedule failed:', calendarError);
          throw new Error(`Calendar reschedule failed: ${calendarError.message || calendarError}`);
        }
        
      } catch (calendarException) {
        console.error('❌ Exception during calendar reschedule:', calendarException);
        throw new Error(`Calendar reschedule failed: ${calendarException.message || calendarException}`);
      }
    } else if (hostUserId) {
      // Create new calendar event for booking that doesn't have one
      
      try {
        // Get meeting link details for calendar event creation
        const { data: meetingLinkData, error: meetingLinkError } = await supabase
          .from('meeting_links')
          .select('id, name, location_type, custom_location, meeting_address')
          .eq('id', bookingData.meeting_link_id)
          .single();

        if (meetingLinkError) {
          console.error('❌ Failed to get meeting link details:', meetingLinkError);
          throw new Error(`Failed to get meeting link details: ${meetingLinkError.message}`);
        }

        // Get host profile for more complete description
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', hostUserId)
          .single();

        // Recreate the original calendar description format
        const locationDescription = meetingLinkData.location_type === 'google_meet' 
          ? 'A Google Meet link will be provided in the calendar invite.'
          : `${meetingLinkData.custom_location || meetingLinkData.meeting_address || meetingLinkData.location_type || 'TBD'}`;

        const baseDescription = `What:
${meetingLinkData.name}

Invitee Time Zone:
UTC

Organizer:
${profileData?.full_name || 'Host'}

Invitees:
${bookingData.booker_name} <${bookingData.booker_email}>

${meetingLinkData.location_type === 'google_meet' ? '' : `Where:
📍 Meeting Type: ${meetingLinkData.location_type || 'Custom'}
   Location: ${locationDescription}`}

~~~~~~~~~~~~~~~~~~~
Powered by ChronoCal, world's first calendar that schedules with your biology so you can do your best work without burning out.

Get yours: https://chronocal.io/?utm_source=calendar&utm_medium=invite&utm_campaign=user_share&utm_content=${meetingLinkData.id}
~~~~~~~~~~~~~~~~~~~`;

        // Add reschedule note if provided
        const finalDescription = reason 
          ? `${baseDescription}\n\nRescheduled: ${reason}`
          : baseDescription;

        // Create calendar event
        const eventData = {
          summary: meetingLinkData.name || 'Meeting',
          description: finalDescription,
          start: {
            dateTime: startTime.toISOString(),
            timeZone: 'UTC'
          },
          end: {
            dateTime: endTime.toISOString(),
            timeZone: 'UTC'
          },
          attendees: [
            {
              email: bookingData.booker_email,
              displayName: bookingData.booker_name
            }
          ]
        };

        const { data: calendarResponse, error: calendarError } = await supabase.functions.invoke('google-calendar', {
          body: {
            action: 'create_event',
            event: eventData,
            userId: hostUserId
          }
        });

        if (calendarError) {
          console.error('❌ Calendar event creation failed:', calendarError);
          throw new Error(`Calendar event creation failed: ${calendarError.message || calendarError}`);
        }

        if (calendarResponse?.id) {
          // Update the booking with the new calendar event ID
          const { error: updateCalendarError } = await supabase
            .from('bookings')
            .update({ calendar_event_id: calendarResponse.id })
            .eq('id', bookingId);

          if (updateCalendarError) {
            console.error('❌ Failed to update booking with calendar event ID:', updateCalendarError);
            // Don't throw here - the calendar event was created successfully
          } else {
            console.log(`✅ Calendar event created and linked: ${calendarResponse.id}`);
          }
        }
        
        console.log('✅ Calendar event created successfully during reschedule');
      } catch (calendarException) {
        console.error('❌ Exception during calendar event creation:', calendarException);
        // Don't throw here - we can still update the booking time even if calendar fails
        console.log('⚠️ Continuing with database update despite calendar creation failure');
      }
    } else {
      console.log('ℹ️ No calendar event to reschedule and no host user ID available', {
        calendar_event_id: bookingData.calendar_event_id,
        hostUserId: hostUserId
      });
    }

    // Step 1.5: Handle Zoom meeting reschedule if it exists
    if (locationType === 'zoom' && bookingData.zoom_meeting_id && hostUserId) {
      try {
        console.log(`📹 Rescheduling Zoom meeting: ${bookingData.zoom_meeting_id} to ${startTime.toISOString()}`);
        
        const zoomResult = await updateZoomMeeting({
          userId: hostUserId,
          meetingId: bookingData.zoom_meeting_id,
          startTime: startTime.toISOString(),
          duration: duration,
          topic: `${bookingData.booker_name} - Rescheduled Meeting`,
          timezone: 'UTC'
        });

        if (zoomResult.success && zoomResult.meeting) {
          console.log('✅ Zoom meeting rescheduled successfully');
          
          // Update the booking with new Zoom meeting details
          await supabase
            .from('bookings')
            .update({
              zoom_join_url: zoomResult.meeting.join_url,
              zoom_start_url: zoomResult.meeting.start_url
            })
            .eq('id', bookingId);
        } else {
          console.error('❌ Zoom meeting reschedule failed:', zoomResult.error);
          // Don't throw here - we can still reschedule the booking even if Zoom fails
        }
      } catch (zoomException) {
        console.error('❌ Exception during Zoom meeting reschedule:', zoomException);
        // Don't throw here - we can still reschedule the booking even if Zoom fails
      }
    } else if (locationType === 'zoom' && !bookingData.zoom_meeting_id) {
      console.log('ℹ️ Zoom location type but no existing Zoom meeting found - this might be a new booking');
    } else {
      console.log('ℹ️ No Zoom meeting to reschedule', {
        location_type: locationType,
        zoom_meeting_id: bookingData.zoom_meeting_id,
        hostUserId: hostUserId
      });
    }

    // Step 2: Update the database ONLY after calendar reschedule succeeds
    console.log('📝 Updating booking date in database...');
    const updateData: any = {
      booking_date: startTime.toISOString()
    };

    // Add reschedule reason to message if provided
    if (reason) {
      updateData.message = reason;
    }

    const { data, error } = await supabase
      .from('bookings')
      .update(updateData)
      .eq('id', bookingId)
      .select()
      .single();
      
    if (error) {
      console.error('❌ Database update failed:', error);
      throw new Error(`Database update failed: ${error.message}`);
    }

    console.log('✅ Booking successfully rescheduled and synchronized');
    return {
      success: true,
      data,
      calendarEventCreated: !bookingData.calendar_event_id && hostUserId
    };
  } catch (error: any) {
    console.error('❌ Error rescheduling booking:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Decline a booking
 */
export const declineBooking = async ({ bookingId, reason }: DeclineBookingParams) => {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .update({
        status: 'canceled',
        message: reason || 'Declined by host'
      })
      .eq('id', bookingId)
      .select()
      .single();
      
    if (error) throw error;
    
    return {
      success: true,
      data
    };
  } catch (error: any) {
    console.error('Error declining booking:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
